import {Box, Flex, Image, keyframes, Skeleton, Text} from '@chakra-ui/react'
import {CommonColors} from '@utils/CommonStyles'
import {useDispatch, useSelector} from 'react-redux'
import VolumeIcon from '@assets/svgs/volume.svg'
import {useEffect, useState} from 'react'
import {
  updateAudio,
  updateIsDisableBtn,
} from '@redux/learn-part-three/learnPartThree'
import learnPartThreeRequest from '@apis/learn-part-three'
import {Tooltip} from 'antd'

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const LoadingSkeleton = () => (
  <Flex gap="16px">
    <Flex flexDirection="column" alignItems="center" gap="8px">
      <Skeleton w="48px" h="48px" borderRadius="full" />
      <Skeleton w="2px" h="60px" />
    </Flex>
    <Flex flexDirection="column" gap="8px">
      <Skeleton h="24px" w="300px" />
      <Skeleton h="24px" w="500px" />
    </Flex>
  </Flex>
)

const PracticeLesson = () => {
  const {practiceData, numberRecorded, sessionId, currentContent, isRecording} =
    useSelector((state: any) => state.learnPartThree)
  const [practiceLessonData, setPracticeLessonData] = useState<any>([])
  const [isPracticed, setIsPracticed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!sessionId) return
    getPracticeLessonBody()
  }, [sessionId])

  useEffect(() => {
    if (practiceData) {
      setIsPracticed(true)
    }
  }, [practiceData])

  useEffect(() => {
    if (!practiceData) return
    calculateAccuracy()
  }, [practiceData])

  useEffect(() => {
    if (numberRecorded > 0) {
      dispatch(updateIsDisableBtn(false))
    } else {
      dispatch(updateIsDisableBtn(true))
    }
  }, [numberRecorded])

  useEffect(() => {
    if (!practiceData) return
    const accuracy = calculateAccuracy()

    if (accuracy > 50) {
      dispatch(updateIsDisableBtn(false))
    }
  }, [practiceData])

  const getPracticeLessonBody = async () => {
    setIsLoading(true)

    try {
      const params = {
        session_id: sessionId,
      }

      const res = await learnPartThreeRequest.getPracticeLessonBody(params)
      setPracticeLessonData(res?.data?.data)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const compareWords = (word: string) => {
    const correctWords = practiceData?.result?.text?.split(' ') || []
    return correctWords.includes(word)
  }

  const calculateAccuracy = () => {
    if (!practiceLessonData) return 0
    const textNoTag = practiceLessonData
      ?.map((item: any) => `${item?.linking_phrase || ''} ${item?.text || ''}`)
      .join(' ')
      .trim()
    const userWords = textNoTag.split(' ')
    const correctCount = userWords.filter((word: string) =>
      compareWords(word),
    ).length
    return (correctCount / userWords.length) * 100
  }

  const handlePlayAudio = (audio: string) => {
    if (isRecording) return

    let audioData = {
      time: Date.now(),
      url: audio || '',
    }

    dispatch(updateAudio(audioData))
  }

  return (
    <Flex flexDirection={'column'} gap={'8px'} maxW={'600px'} mx={'auto'}>
      {isLoading ? (
        <>
          <LoadingSkeleton />
          <LoadingSkeleton />
        </>
      ) : (
        practiceLessonData.map((item: any, index: number) => (
          <Flex
            key={index}
            className={'outline-item'}
            gap={'16px'}
            animation={`${slideUp} 0.5s ease-out forwards`}
            style={{
              opacity: 0,
              animationDelay: `${(index + 1) * 0.4}s`,
            }}>
            <Flex
              flexShrink={0}
              flexDirection={'column'}
              minH={'100px'}
              alignItems={'center'}
              gap={'8px'}>
              <Image
                src={VolumeIcon}
                alt="flag"
                width={'48px'}
                height={'48px'}
                cursor={'pointer'}
                onClick={() => handlePlayAudio(item?.audio)}
              />
              <Box flex={1} w={'2px'} bg={'#E4E7EC'} />
            </Flex>
            <Flex flexDirection={'column'} pb={'24px'}>
              <Text fontSize={'16px'} fontWeight={'600'}>
                {item?.header}
              </Text>
              <Flex gap={'4px'} alignItems={'center'} flexWrap={'wrap'}>
                <Tooltip title={currentContent?.data?.popup?.idea}>
                  <Flex gap={'4px'} cursor={'pointer'}>
                    {item?.linking_phrase
                      ?.split(' ')
                      .map((word: any, index: number) => (
                        <Text
                          key={index}
                          as={'span'}
                          fontSize={'18px'}
                          color={
                            isPracticed
                              ? compareWords(word)
                                ? CommonColors.primary
                                : 'red'
                              : CommonColors.primary
                          }>
                          {word}
                        </Text>
                      ))}
                  </Flex>
                </Tooltip>
                {item?.text?.split(' ').map((word: any, index: number) => (
                  <Text
                    key={index}
                    as={'span'}
                    fontSize={'18px'}
                    color={
                      isPracticed
                        ? compareWords(word)
                          ? CommonColors.primary
                          : 'red'
                        : '#475467'
                    }>
                    {word}
                  </Text>
                ))}
              </Flex>
            </Flex>
          </Flex>
        ))
      )}
    </Flex>
  )
}

export default PracticeLesson
